import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import {
  commitLink,
  fetchReleases,
  shortSha,
  type Release,
  type ReleaseBrief,
  type ReleaseComponent,
  type ReleaseEnvironment,
  type ReleaseMethod,
  type ReleasesResponse,
} from "../services/releasesService";
import { dateTimeLabel, localeOf, presetRange, today, type RangePreset } from "../lib/heatmap";
import { describeRequestFailure } from "../lib/requestFailure";
import { buildThemeClasses, inputClass } from "../theme";

// ---------------------------------------------------------------------------
// Releases y despliegues (#998, épica #1002): versión actual de QA y prod de
// cada plataforma, cada cuánto se despliega, si lo hizo una persona (quién) o
// GitHub Actions / automatización, a partir de qué PR, los commits y PRs del
// periodo y las notas de cada release. Solo administradores.
// ---------------------------------------------------------------------------

const ENVS: ReleaseEnvironment[] = ["qa", "prod"];
const COMPONENTS: ReleaseComponent[] = ["frontend", "backend"];
const METHOD_TONES: Record<ReleaseMethod, string> = {
  manual: "bg-amber-500/15 text-amber-500",
  github_actions: "bg-sky-500/15 text-sky-500",
  automation: "bg-violet-500/15 text-violet-500",
};
const ENV_TONES: Record<ReleaseEnvironment, string> = {
  qa: "bg-sky-500/15 text-sky-500",
  prod: "bg-emerald-500/15 text-emerald-500",
};

const who = (p: ReleaseBrief["deployedBy"] | undefined) => (p && (p.name || p.login || p.email)) || "—";

export default function ReleasesSection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const locale = localeOf(i18n?.language);
  const card = `rounded-2xl border p-4 ${tc.cardBg} ${tc.border}`;

  const [preset, setPreset] = useState<RangePreset>("30d");
  const [range, setRange] = useState(presetRange("30d"));
  const [platform, setPlatform] = useState("");
  const [environment, setEnvironment] = useState("");
  const [data, setData] = useState<ReleasesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  // Solo la última petición pinta: cambiar filtros rápido no deja datos viejos.
  const requestId = useRef(0);
  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setErrorKey(null);
    try {
      const next = await fetchReleases({ from: range.from, to: range.to, platform: platform || undefined, environment: environment || undefined });
      if (id === requestId.current) setData(next);
    } catch (e) {
      if (id !== requestId.current) return;
      setData(null);
      // Un 403 no es un fallo: la sección es de administradores y se dice así.
      const failure = describeRequestFailure(e);
      setErrorKey(
        failure.kind === "permission"
          ? "workHours.releases.adminOnly"
          : failure.kind === "unconfirmed"
            ? "workHours.releases.timeout"
            : "workHours.releases.loadError",
      );
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [range.from, range.to, platform, environment]);

  useEffect(() => {
    void load();
  }, [load]);

  const m = data?.metrics;
  const platforms = useMemo(() => (data?.current ?? []).map((r) => r.platform), [data]);
  const methodLabel = (method: ReleaseMethod) => t(`workHours.releases.method.${method}`);

  // Funciones de render (no componentes anidados): un componente definido
  // dentro del cuerpo se desmonta y se vuelve a montar en cada render.
  const renderCell = (r: ReleaseBrief | null, repo: string | null) =>
    r ? (
      <div className="space-y-0.5" data-testid="release-cell">
        <div className={`font-semibold ${tc.text}`}>
          {r.version || shortSha(r.sha)}
          {repo && (
            <a href={commitLink(repo, r.sha)} target="_blank" rel="noopener noreferrer" className="ml-1 font-mono text-[11px] text-violet-500 hover:underline">
              {shortSha(r.sha)}
            </a>
          )}
        </div>
        <div className={`text-[11px] ${tc.textSecondary}`}>{dateTimeLabel(r.deployedAt, locale)}</div>
        <div className="flex flex-wrap items-center gap-1 text-[11px]">
          <span className={`rounded-full px-1.5 py-0.5 ${METHOD_TONES[r.method]}`}>{methodLabel(r.method)}</span>
          <span className={tc.textSecondary}>{who(r.deployedBy)}</span>
        </div>
        {r.sourcePr && (
          <a href={r.sourcePr.url || undefined} target="_blank" rel="noopener noreferrer" className="block truncate text-[11px] text-violet-500 hover:underline" title={r.sourcePr.title || ""}>
            PR #{r.sourcePr.number}
          </a>
        )}
      </div>
    ) : (
      <span className={`text-xs ${tc.textSecondary}`}>—</span>
    );

  return (
    <div className="w-full max-w-7xl space-y-4 overflow-y-auto p-4 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${tc.text}`}>{t("workHours.menu.releases")}</h1>
          <p className={`text-sm ${tc.textSecondary}`}>{t("workHours.releases.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(["7d", "30d", "90d"] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                setPreset(p);
                setRange(presetRange(p));
              }}
              className={`rounded-lg px-3 py-2 text-xs font-medium ${preset === p ? "bg-violet-600 text-white" : `${tc.buttonHover} ${tc.textSecondary} border ${tc.border}`}`}
            >
              {t(`workHours.range.${p}`)}
            </button>
          ))}
          <input
            type="date"
            aria-label={t("workHours.filters.from")}
            value={range.from}
            max={range.to}
            onChange={(e) => {
              setPreset("custom");
              setRange((r) => ({ ...r, from: e.target.value }));
            }}
            className={inputClass(isDarkMode)}
          />
          <input
            type="date"
            aria-label={t("workHours.filters.to")}
            value={range.to}
            min={range.from}
            max={today()}
            onChange={(e) => {
              setPreset("custom");
              setRange((r) => ({ ...r, to: e.target.value }));
            }}
            className={inputClass(isDarkMode)}
          />
          <select aria-label={t("workHours.releases.platform")} value={platform} onChange={(e) => setPlatform(e.target.value)} className={inputClass(isDarkMode)}>
            <option value="">{t("workHours.releases.allPlatforms")}</option>
            {platforms.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select aria-label={t("workHours.releases.environment")} value={environment} onChange={(e) => setEnvironment(e.target.value)} className={inputClass(isDarkMode)}>
            <option value="">{t("workHours.releases.allEnvironments")}</option>
            {ENVS.map((env) => (
              <option key={env} value={env}>
                {t(`workHours.releases.env.${env}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {errorKey && (
        <p data-testid="releases-error" className="rounded-lg bg-red-500/10 p-3 text-sm text-red-500">
          {t(errorKey)}
        </p>
      )}
      {data && !data.githubConfigured && (
        <p className="rounded-lg bg-amber-500/10 p-3 text-sm text-amber-600">{t("workHours.releases.githubMissing")}</p>
      )}
      {data?.truncated && <p className="rounded-lg bg-amber-500/10 p-3 text-sm text-amber-600">{t("workHours.releases.truncated")}</p>}
      {loading && !data && <p className={`text-sm ${tc.textSecondary}`}>{t("workHours.loading")}</p>}

      {data && m && (
        <>
          {/* Versión actual de QA y prod por plataforma */}
          <div className={card}>
            <h2 className={`mb-3 text-sm font-semibold ${tc.text}`}>{t("workHours.releases.current")}</h2>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm" data-testid="releases-current">
                <thead>
                  <tr className={`text-left text-xs ${tc.textSecondary}`}>
                    <th className="py-1 pr-3" rowSpan={2}>
                      {t("workHours.releases.platform")}
                    </th>
                    {ENVS.map((env) => (
                      <th key={env} colSpan={2} className="py-1 pr-3">
                        <span className={`rounded-full px-2 py-0.5 ${ENV_TONES[env]}`}>{t(`workHours.releases.env.${env}`)}</span>
                      </th>
                    ))}
                  </tr>
                  <tr className={`text-left text-[11px] ${tc.textSecondary}`}>
                    {ENVS.flatMap((env) => COMPONENTS.map((c) => <th key={`${env}-${c}`} className="py-1 pr-3 font-normal">{t(`workHours.releases.component.${c}`)}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {data.current.map((row) => (
                    <tr key={row.platform} className={`border-t align-top ${tc.border}`} data-testid={`current-${row.platform}`}>
                      <td className={`py-2 pr-3 font-medium ${tc.text}`}>
                        {row.platform}
                        {row.repo && <div className={`text-[11px] font-normal ${tc.textSecondary}`}>{row.repo}</div>}
                      </td>
                      {ENVS.flatMap((env) =>
                        COMPONENTS.map((c) => (
                          <td key={`${env}-${c}`} className="py-2 pr-3">
                            {renderCell(row[env][c], row.repo)}
                          </td>
                        )),
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Métricas del rango */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="releases-kpis">
            {[
              { label: t("workHours.releases.kpi.deploys"), value: String(m.total), hint: t("workHours.releases.kpi.byEnv", { qa: m.byEnvironment.qa, prod: m.byEnvironment.prod }) },
              { label: t("workHours.releases.kpi.frequency"), value: m.avgDaysBetweenProd == null ? "—" : t("workHours.releases.kpi.days", { count: m.avgDaysBetweenProd }), hint: t("workHours.releases.kpi.frequencyHint") },
              { label: t("workHours.releases.kpi.manual"), value: `${m.manualShare}%`, hint: t("workHours.releases.kpi.methods", { manual: m.byMethod.manual, gh: m.byMethod.github_actions, auto: m.byMethod.automation }) },
              { label: t("workHours.releases.kpi.commits"), value: String(m.commitCount) },
              { label: t("workHours.releases.kpi.prs"), value: String(m.pullRequestCount) },
            ].map((k) => (
              <div key={k.label} className={card}>
                <p className={`text-xs ${tc.textSecondary}`}>{k.label}</p>
                <p className={`mt-1 text-2xl font-bold ${tc.text}`}>{k.value}</p>
                {k.hint && <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>{k.hint}</p>}
              </div>
            ))}
          </div>

          {m.byPerson.length > 0 && (
            <div className={card}>
              <h2 className={`mb-2 text-sm font-semibold ${tc.text}`}>{t("workHours.releases.byPerson")}</h2>
              <div className="flex flex-wrap gap-2 text-xs">
                {m.byPerson.map((p) => (
                  <span key={p.key} className={`rounded-full border px-2 py-1 ${tc.border} ${tc.text}`}>
                    {p.name} · {p.count} ({t("workHours.releases.env.prod")}: {p.prod})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Releases del rango */}
          <div className={card}>
            <h2 className={`mb-3 text-sm font-semibold ${tc.text}`}>{t("workHours.releases.list")}</h2>
            {data.releases.length === 0 ? (
              <p className={`text-xs ${tc.textSecondary}`}>{t("workHours.releases.empty")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm" data-testid="releases-list">
                  <thead>
                    <tr className={`text-left text-xs ${tc.textSecondary}`}>
                      <th className="py-1 pr-2">{t("workHours.releases.date")}</th>
                      <th className="py-1 pr-2">{t("workHours.releases.platform")}</th>
                      <th className="py-1 pr-2">{t("workHours.releases.environment")}</th>
                      <th className="py-1 pr-2">{t("workHours.releases.version")}</th>
                      <th className="py-1 pr-2">{t("workHours.releases.who")}</th>
                      <th className="py-1 pr-2">{t("workHours.releases.sourcePr")}</th>
                      <th className="py-1 pr-2 text-right">{t("workHours.releases.changes")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.releases.map((r) => renderRow(r, open === r.id, () => setOpen(open === r.id ? null : r.id)))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Commits y PRs del periodo */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className={card}>
              <h2 className={`mb-2 text-sm font-semibold ${tc.text}`}>{t("workHours.releases.periodPrs", { count: m.pullRequestCount })}</h2>
              <ul className="max-h-80 space-y-1 overflow-y-auto text-xs">
                {m.pullRequests.map((p) => (
                  <li key={`${p.repo}#${p.number}`} className={tc.text}>
                    <a href={p.url || undefined} target="_blank" rel="noopener noreferrer" className="text-violet-500 hover:underline">
                      {(p.repo || "").split("/")[1]}#{p.number}
                    </a>{" "}
                    {p.title}
                  </li>
                ))}
                {m.pullRequests.length === 0 && <li className={tc.textSecondary}>{t("workHours.releases.none")}</li>}
              </ul>
            </div>
            <div className={card}>
              <h2 className={`mb-2 text-sm font-semibold ${tc.text}`}>{t("workHours.releases.periodCommits", { count: m.commitCount })}</h2>
              <ul className="max-h-80 space-y-1 overflow-y-auto text-xs">
                {m.commits.map((c) => (
                  <li key={`${c.repo}@${c.sha}`} className={tc.text}>
                    <a href={c.url || (c.repo ? commitLink(c.repo, c.sha) : undefined)} target="_blank" rel="noopener noreferrer" className="font-mono text-violet-500 hover:underline">
                      {shortSha(c.sha)}
                    </a>{" "}
                    {c.message} <span className={tc.textSecondary}>· {c.author || "—"}</span>
                  </li>
                ))}
                {m.commits.length === 0 && <li className={tc.textSecondary}>{t("workHours.releases.none")}</li>}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );

  function renderRow(r: Release, open: boolean, onToggle: () => void) {
    return (
      <Fragment key={r.id}>
        <tr className={`cursor-pointer border-t align-top ${tc.border} ${tc.text} ${tc.buttonHover}`} onClick={onToggle} data-testid={`release-${r.id}`}>
          <td className="whitespace-nowrap py-1.5 pr-2 text-xs">{dateTimeLabel(r.deployedAt, locale)}</td>
          <td className="py-1.5 pr-2">
            {r.platform}
            <div className={`text-[11px] ${tc.textSecondary}`}>{t(`workHours.releases.component.${r.component}`)}</div>
          </td>
          <td className="py-1.5 pr-2">
            <span className={`rounded-full px-2 py-0.5 text-[11px] ${ENV_TONES[r.environment]}`}>{t(`workHours.releases.env.${r.environment}`)}</span>
          </td>
          <td className="py-1.5 pr-2 text-xs">
            {r.previousVersion && r.previousVersion !== r.version ? `${r.previousVersion} → ` : ""}
            <span className="font-semibold">{r.version || shortSha(r.sha)}</span>
          </td>
          <td className="py-1.5 pr-2 text-xs">
            <span className={`mr-1 rounded-full px-1.5 py-0.5 ${METHOD_TONES[r.method]}`}>{methodLabel(r.method)}</span>
            {who(r.deployedBy)}
          </td>
          <td className="py-1.5 pr-2 text-xs">
            {r.sourcePr ? (
              <a href={r.sourcePr.url || undefined} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-violet-500 hover:underline">
                #{r.sourcePr.number}
              </a>
            ) : (
              "—"
            )}
          </td>
          <td className="py-1.5 pr-2 text-right text-xs">
            {t("workHours.releases.counts", { commits: r.commits.length, prs: r.pullRequests.length })}
            <i className={`ml-1 ${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
          </td>
        </tr>
        {open && (
          <tr className={`border-t ${tc.border}`}>
            <td colSpan={7} className="py-3">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid={`release-detail-${r.id}`}>
                <div>
                  <p className={`mb-1 text-xs font-semibold ${tc.text}`}>
                    {t("workHours.releases.notes")}
                    {r.notesSource === "pull_requests" && <span className={`ml-1 font-normal ${tc.textSecondary}`}>({t("workHours.releases.notesFromPrs")})</span>}
                  </p>
                  <p className={`whitespace-pre-wrap text-xs ${tc.text}`}>{r.notes || t("workHours.releases.noNotes")}</p>
                  {r.runUrl && (
                    <a href={r.runUrl} target="_blank" rel="noopener noreferrer" className="mt-2 block text-xs text-violet-500 hover:underline">
                      {t("workHours.releases.run")}
                    </a>
                  )}
                  {r.target && <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>{r.target}</p>}
                  {r.enrichment && r.enrichment.status !== "done" && (
                    <p className="mt-1 text-[11px] text-amber-600">{t("workHours.releases.notEnriched", { reason: r.enrichment.reason || r.enrichment.status })}</p>
                  )}
                  {r.enrichment?.commitsTruncated && (
                    <p className="mt-1 text-[11px] text-amber-600">{t("workHours.releases.commitsTruncated", r.enrichment.commitsTruncated)}</p>
                  )}
                </div>
                <div>
                  <p className={`mb-1 text-xs font-semibold ${tc.text}`}>{t("workHours.releases.prs", { count: r.pullRequests.length })}</p>
                  <ul className="space-y-0.5 text-xs">
                    {r.pullRequests.map((p) => (
                      <li key={p.number}>
                        <a href={p.url || undefined} target="_blank" rel="noopener noreferrer" className="text-violet-500 hover:underline">
                          #{p.number}
                        </a>{" "}
                        <span className={tc.text}>{p.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className={`mb-1 text-xs font-semibold ${tc.text}`}>
                    {t("workHours.releases.commits", { count: r.commits.length })}
                    {r.previousSha && <span className={`ml-1 font-mono font-normal ${tc.textSecondary}`}>{shortSha(r.previousSha)}…{shortSha(r.sha)}</span>}
                  </p>
                  <ul className="max-h-48 space-y-0.5 overflow-y-auto text-xs">
                    {r.commits.map((c) => (
                      <li key={c.sha} className={tc.text}>
                        <a href={c.url || commitLink(r.repo, c.sha)} target="_blank" rel="noopener noreferrer" className="font-mono text-violet-500 hover:underline">
                          {shortSha(c.sha)}
                        </a>{" "}
                        {c.message}
                      </li>
                    ))}
                    {r.commits.length === 0 && <li className={tc.textSecondary}>{r.enrichment?.firstRelease ? t("workHours.releases.firstRelease") : t("workHours.releases.none")}</li>}
                  </ul>
                </div>
              </div>
            </td>
          </tr>
        )}
      </Fragment>
    );
  }
}
